import { useQuery } from '@tanstack/react-query';
import {
  entityLabelResolverFor,
  type EntityLabelInput,
} from '../utils/entity-label-registry';
import { aiQueryKeys } from './query-keys';

/**
 * Resolve an entity reference (pluginId + entityType + entityId) to a
 * human-readable label like "PO-2026-00014" or "SHIP-ABC123-XYZ".
 *
 * Returns `{ label, isPending }`. Falls back to `null` if no resolver is
 * registered for this {pluginId, entityType} pair, or if the lookup fails.
 * Cached and deduplicated per entity by React Query.
 */
export const useEntityLabel = (scope: EntityLabelInput) => {
  const resolver = entityLabelResolverFor(scope);
  const enabled = Boolean(resolver && scope.entityId);

  const query = useQuery({
    queryKey: enabled
      ? [...aiQueryKeys.all, 'entity-label', scope.pluginId, scope.entityType, scope.entityId]
      : [...aiQueryKeys.all, 'entity-label', 'disabled'],
    queryFn: () => resolver!(scope.entityId!),
    enabled,
    staleTime: 5 * 60_000,
  });

  return { label: query.data ?? null, isPending: query.isPending && enabled };
};
